import { decodeOneOf, encodeOneOf, METRIC_CODES, safeId, safePage, safeSort } from '../../../../../domain';
import { PATIENT_PERIOD_CODES } from '../../../../../components/patient-analytics';
import { periodRange } from '../../../../../components/period';
import { CollaboratorsKeys } from '../collaborators.key';
import { Places } from '../../../../route-consts';
import { HISTORY_SORT_FIELDS } from '../../../../generic/patient-detail/history-table';
/** Prefixo dos nomes deste arquivo na intencao - unico na aplicacao (ver `url-codes.test.ts`). */
const PREFIX = 'cd';
/** Os parametros deste place: vao para o endereco. */
const P = {
    PatientId: `${PREFIX}a`,
    /** Qual dia do historico esta aberto - era `showMeasurementByDay`, um useState. */
    Day: `${PREFIX}b`,
    /**
     * A ordenacao e a pagina da tabela de HISTORICO.
     *
     * Separadas de `sort` e `page`, que sao da LISTA: os dois chegam no detalhe e
     * voltam com ele quando o leitor clica em "Dados do Colaborador". Reusar as
     * mesmas chaves faria a tabela do detalhe reordenar a lista ao voltar.
     */
    HistorySort: `${PREFIX}c`,
    HistoryPage: `${PREFIX}d`,
    /**
     * Os filtros do painel do paciente embutido.
     *
     * Prefixados porque o painel e desenhado em TRES lugares - aqui, no detalhe do
     * monitoramento e no historico compartilhado - e quem publica e o lugar, nao o
     * painel. O vocabulario, esse e um so: `PATIENT_PERIOD_CODES`.
     *
     * `DetailFrom` guarda o codigo que era da data de referencia. E o mesmo lugar do
     * endereco dizendo a mesma coisa de outro jeito - onde a janela comeca -, entao
     * gastar um codigo novo e deixar o antigo morto seria pior.
     */
    DetailPeriod: `${PREFIX}e`,
    DetailFrom: `${PREFIX}f`,
    DetailTo: `${PREFIX}g`,
    DetailMetric: `${PREFIX}h`
};
/** Os atributos deste place: viajam na intencao, mas nao vao para o endereco. */
const A = {
    /** Um paciente ja em maos, para abrir o detalhe nao buscar de novo. */
    patientDetail_row: `${PREFIX}i`
};
/**
 * Qual paciente o ADMIN esta olhando, vindo da lista de Colaboradores.
 *
 * Estende as keys da lista que hospeda esta montagem, entao o place e CONSTANTE e a
 * viagem parte de `CollaboratorDetailKeys.of(app)` como em qualquer outra tela. Herdar da
 * hierarquia de composicao tambem e o que da acesso aos parametros da lista - quem
 * esta mais fundo no caminho pode consumir o que quem esta acima publica.
 *
 * `implements PatientDetailKeys` e o contrato: o apresentador generico depende da
 * INTERFACE, nunca desta classe. Esquecer um parametro aqui nao compila, e e isso
 * que mantem as duas montagens dizendo a mesma coisa sem herdarem uma da outra.
 *
 * Os oito parametros sao os mesmos nos dois pontos de montagem, e isso nao cria dois
 * donos para um nome: as montagens sao mutuamente exclusivas - nenhuma e ancestral
 * da outra -, entao so uma publica de cada vez.
 */
export class CollaboratorDetailKeys extends CollaboratorsKeys {
    get place() {
        return Places.collaboratorDetail;
    }
    /**
     * O endereco do detalhe de um paciente, em texto claro, para o `href` do nome na
     * lista. Quem sela e o `MainPresenter.sealedAddress`; o nome do parametro so esta
     * classe conhece.
     */
    static plainAddressOf(patientId) {
        return `${Places.collaboratorDetail.name}?${P.PatientId}=${patientId}`;
    }
    // :: paciente
    get patientId() {
        return safeId(this._intent.getParameterAsNumber(P.PatientId));
    }
    set patientId(value) {
        this._intent.setParameter(P.PatientId, value);
    }
    // :: dia aberto
    get day() {
        return this._intent.getParameterAsString(P.Day);
    }
    set day(value) {
        this._intent.setParameter(P.Day, value ? value : undefined);
    }
    // :: a tabela de historico, que tem ordenacao e pagina PROPRIAS
    get historySort() {
        return safeSort(this._intent.getParameterAsString(P.HistorySort), HISTORY_SORT_FIELDS);
    }
    set historySort(value) {
        this._intent.setParameter(P.HistorySort, value ? value : undefined);
    }
    get historyPage() {
        return safePage(this._intent.getParameterAsNumber(P.HistoryPage));
    }
    set historyPage(value) {
        this._intent.setParameter(P.HistoryPage, value > 1 ? value : undefined);
    }
    // :: painel de analytics
    /**
     * Se o ENDERECO diz um periodo. Quando nao diz, vale o ultimo periodo escolhido no
     * painel (B-35, `recallPeriod`), e nao o padrao fixo desta tela.
     */
    get periodInAddress() {
        return (this._intent.getParameterAsString(P.DetailPeriod) !== undefined ||
            this._intent.getParameterAsString(P.DetailFrom) !== undefined ||
            this._intent.getParameterAsString(P.DetailTo) !== undefined);
    }
    get period() {
        return decodeOneOf(this._intent.getParameterAsString(P.DetailPeriod), PATIENT_PERIOD_CODES, '7d');
    }
    set period(value) {
        this._intent.setParameter(P.DetailPeriod, value === '7d' ? undefined : encodeOneOf(value, PATIENT_PERIOD_CODES));
    }
    /**
     * As pontas do intervalo - lidas do atalho, ou do endereco quando ele e `custom`.
     *
     * So o `custom` as ESCREVE: nos outros o intervalo e derivado do atalho, e
     * repetir na URL o que o atalho ja diz seria ruido - e pior, poderia discordar
     * dele depois que o dia virasse.
     */
    get analyticsFrom() {
        return this._intent.getParameterAsString(P.DetailFrom) ?? periodRange(this.period).from;
    }
    set analyticsFrom(value) {
        this._intent.setParameter(P.DetailFrom, this.period === 'custom' ? value : undefined);
    }
    get analyticsTo() {
        return this._intent.getParameterAsString(P.DetailTo) ?? periodRange(this.period).to;
    }
    set analyticsTo(value) {
        this._intent.setParameter(P.DetailTo, this.period === 'custom' ? value : undefined);
    }
    get metric() {
        return decodeOneOf(this._intent.getParameterAsString(P.DetailMetric), METRIC_CODES, 'FREQUENCIA_CARDIACA');
    }
    set metric(value) {
        this._intent.setParameter(P.DetailMetric, value === 'FREQUENCIA_CARDIACA' ? undefined : encodeOneOf(value, METRIC_CODES));
    }
    // :: linha ja em maos
    get row() {
        return this._intent.attributes.get(A.patientDetail_row);
    }
    set row(value) {
        this._intent.attributes.set(A.patientDetail_row, value);
    }
}
//# sourceMappingURL=collaborator-detail.key.js.map